// components/admin/AgentMonitoring.js
import React, { useState, useEffect, useRef } from 'react';
import { Eye, Search, User, Clock, MessageCircle, Filter } from 'react-feather';
import AuthService from '../../services/authService';

const AgentMonitoring = () => {
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState('');
  
  // Filters
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  
  const messagesEndRef = useRef(null);
  const API_URL = 'https://back-end-x7tx.onrender.com/api';

  useEffect(() => {
    fetchAgents();
    fetchConversations();
  }, []);

  useEffect(() => {
    filterConversations();
  }, [selectedAgentId, userSearchTerm, conversations]);

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/agents`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setAgents(data.data);
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
    }
  };

  const fetchConversations = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/agent-conversations`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setConversations(data.data);
        setFilteredConversations(data.data);
      } else {
        setError('Failed to fetch conversations');
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Error loading conversations');
    } finally {
      setIsLoading(false);
    }
  };

  const filterConversations = () => {
    let filtered = [...conversations];

    // Filter by agent
    if (selectedAgentId) {
      filtered = filtered.filter(conv => conv.helper && conv.helper._id === selectedAgentId);
    }

    // Filter by user search
    if (userSearchTerm) {
      const search = userSearchTerm.toLowerCase();
      filtered = filtered.filter(conv => 
        conv.user && (
          conv.user.name.toLowerCase().includes(search) ||
          conv.user.email.toLowerCase().includes(search)
        )
      );
    }

    setFilteredConversations(filtered);
  };

  const loadConversation = async (conversationId) => {
    setIsLoadingMessages(true);
    try {
      const response = await fetch(`${API_URL}/admin/agent-conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setSelectedConversation(data.data.conversation);
        setMessages(data.data.messages || []);
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError('Failed to load messages');
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return `${Math.floor(diffInHours * 60)}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return messageDate.toLocaleDateString();
    }
  };

  const clearFilters = () => {
    setSelectedAgentId('');
    setUserSearchTerm('');
  };

  const styles = {
    container: {
      display: 'flex',
      height: 'calc(100vh - 80px)',
      backgroundColor: '#0f1419',
      borderRadius: '12px',
      overflow: 'hidden',
      border: '1px solid rgba(99, 102, 241, 0.2)'
    },
    sidebar: {
      width: '380px',
      borderRight: '1px solid rgba(99, 102, 241, 0.2)',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'rgba(15, 20, 25, 0.8)'
    },
    header: {
      padding: '20px',
      borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
      backgroundColor: 'rgba(15, 20, 25, 0.8)'
    },
    title: {
      margin: '0 0 16px 0',
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    filtersSection: {
      padding: '16px',
      borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
      backgroundColor: 'rgba(15, 20, 25, 0.8)'
    },
    filterLabel: {
      fontSize: '0.75rem',
      color: 'rgba(255, 255, 255, 0.6)',
      marginBottom: '6px',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    select: {
      width: '100%',
      padding: '8px 12px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '6px',
      color: '#ffffff',
      fontSize: '0.9rem',
      marginBottom: '12px',
      outline: 'none'
    },
    searchInput: {
      width: '100%',
      padding: '8px 12px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '6px',
      color: '#ffffff',
      fontSize: '0.9rem',
      outline: 'none'
    },
    clearFiltersBtn: {
      marginTop: '8px',
      padding: '6px 12px',
      backgroundColor: 'transparent',
      border: '1px solid rgba(99, 102, 241, 0.3)',
      borderRadius: '6px',
      color: '#a5b4fc',
      fontSize: '0.75rem',
      cursor: 'pointer',
      width: '100%',
      transition: 'all 0.2s'
    },
    conversationsList: {
      flex: 1,
      overflowY: 'auto',
      padding: '8px 0'
    },
    conversationItem: (isSelected) => ({
      padding: '16px 20px',
      borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
      cursor: 'pointer',
      backgroundColor: isSelected ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
      transition: 'background-color 0.2s'
    }),
    conversationInfo: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px'
    },
    avatar: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      backgroundColor: 'rgba(99, 102, 241, 0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      border: '2px solid rgba(99, 102, 241, 0.3)',
      color: '#a5b4fc',
      fontSize: '1.2rem',
      fontWeight: '600'
    },
    conversationDetails: {
      flex: 1,
      minWidth: 0
    },
    agentName: {
      fontSize: '0.85rem',
      color: 'rgba(255, 255, 255, 0.5)',
      marginBottom: '4px'
    },
    userName: {
      fontWeight: '600',
      color: '#ffffff',
      fontSize: '0.95rem',
      marginBottom: '4px'
    },
    lastMessage: {
      fontSize: '0.85rem',
      color: 'rgba(255, 255, 255, 0.6)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      marginBottom: '6px'
    },
    conversationMeta: {
      display: 'flex',
      gap: '12px',
      fontSize: '0.75rem',
      color: 'rgba(255, 255, 255, 0.5)'
    },
    badge: {
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '0.7rem',
      fontWeight: '600',
      textTransform: 'capitalize'
    },
    messagePanel: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#0f1419'
    },
    monitoringHeader: {
      padding: '20px',
      borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
      backgroundColor: 'rgba(15, 20, 25, 0.8)'
    },
    readOnlyBanner: {
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      border: '1px solid rgba(245, 158, 11, 0.3)',
      borderRadius: '8px',
      padding: '12px 16px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#fbbf24'
    },
    conversationHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    conversationTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: '#ffffff',
      marginBottom: '8px'
    },
    conversationSubtitle: {
      fontSize: '0.9rem',
      color: 'rgba(255, 255, 255, 0.6)'
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      backgroundColor: '#0f1419'
    },
    message: (isAgent) => ({
      display: 'flex',
      justifyContent: isAgent ? 'flex-end' : 'flex-start',
      marginBottom: '16px'
    }),
    messageBubble: (isAgent) => ({
      maxWidth: '70%',
      padding: '12px 16px',
      borderRadius: '12px',
      backgroundColor: isAgent ? '#2a2a2a' : 'rgba(99, 102, 241, 0.15)',
      border: isAgent ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(99, 102, 241, 0.3)',
      color: '#ffffff'
    }),
    messageHeader: {
      fontSize: '0.75rem',
      color: 'rgba(255, 255, 255, 0.6)',
      marginBottom: '4px',
      fontWeight: '600'
    },
    messageContent: {
      fontSize: '0.95rem',
      lineHeight: '1.5',
      wordWrap: 'break-word',
      textAlign: 'left'
    },
    messageTime: {
      fontSize: '0.7rem',
      color: 'rgba(255, 255, 255, 0.5)',
      marginTop: '6px',
      textAlign: 'right'
    },
    emptyState: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'rgba(255, 255, 255, 0.5)',
      padding: '40px'
    },
    emptyStateIcon: {
      width: '80px',
      height: '80px',
      marginBottom: '20px',
      opacity: 0.3
    },
    emptyStateTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: 'rgba(255, 255, 255, 0.7)',
      marginBottom: '8px'
    },
    emptyStateText: {
      fontSize: '0.9rem',
      color: 'rgba(255, 255, 255, 0.5)',
      textAlign: 'center'
    }
  };

  return (
    <div style={styles.container}>
      {/* Left Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            <Eye size={24} />
            Agent Monitoring
          </h2>
        </div>

        {/* Filters */}
        <div style={styles.filtersSection}>
          <div>
            <div style={styles.filterLabel}>Filter by Agent</div>
            <select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              style={styles.select}
            >
              <option value="">All Agents</option>
              {agents.map(agent => (
                <option key={agent._id} value={agent._id}>
                  {agent.name} {agent.agentInfo?.agency ? `(${agent.agentInfo.agency})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div style={styles.filterLabel}>Search User</div>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
          </div>

          {(selectedAgentId || userSearchTerm) && (
            <button
              onClick={clearFilters}
              style={styles.clearFiltersBtn}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* Conversations List */}
        <div style={styles.conversationsList}>
          {isLoading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>
              Loading conversations...
            </div>
          ) : filteredConversations.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}>
              No agent conversations found
            </div>
          ) : (
            filteredConversations
              .filter(conv => conv.user && conv.helper) // Skip conversations with null user or helper
              .map(conv => (
              <div
                key={conv._id}
                onClick={() => loadConversation(conv._id)}
                style={styles.conversationItem(selectedConversation?._id === conv._id)}
                onMouseEnter={(e) => {
                  if (selectedConversation?._id !== conv._id) {
                    e.currentTarget.style.backgroundColor = 'rgba(99, 102, 241, 0.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedConversation?._id !== conv._id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div style={styles.conversationInfo}>
                  <div style={styles.avatar}>
                    {conv.user.name.charAt(0).toUpperCase()}
                  </div>
                  <div style={styles.conversationDetails}>
                    <div style={styles.agentName}>
                      Agent: {conv.helper.name}
                    </div>
                    <div style={styles.userName}>
                      {conv.user.name}
                      {conv.user.leadTier && (
                        <span style={{
                          ...styles.badge,
                          marginLeft: '8px',
                          backgroundColor: {
                            bronze: 'rgba(205, 127, 50, 0.2)',
                            silver: 'rgba(192, 192, 192, 0.2)',
                            gold: 'rgba(255, 215, 0, 0.2)',
                            platinum: 'rgba(229, 228, 226, 0.2)'
                          }[conv.user.leadTier],
                          color: {
                            bronze: '#cd7f32',
                            silver: '#c0c0c0',
                            gold: '#ffd700',
                            platinum: '#e5e4e2'
                          }[conv.user.leadTier]
                        }}>
                          {conv.user.leadTier}
                        </span>
                      )}
                    </div>
                    {conv.lastMessage && (
                      <div style={styles.lastMessage}>
                        {conv.lastMessage.content}
                      </div>
                    )}
                    <div style={styles.conversationMeta}>
                      <span>
                        <MessageCircle size={12} style={{ marginRight: '4px' }} />
                        {conv.messageCount} messages
                      </span>
                      <span>
                        <Clock size={12} style={{ marginRight: '4px' }} />
                        {formatTime(conv.updatedAt)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Messages */}
      <div style={styles.messagePanel}>
        {!selectedConversation ? (
          <div style={styles.emptyState}>
            <Eye size={80} style={styles.emptyStateIcon} />
            <div style={styles.emptyStateTitle}>Monitor Agent Conversations</div>
            <div style={styles.emptyStateText}>
              Select a conversation from the list to view the full agent-user thread
            </div>
          </div>
        ) : (
          <>
            <div style={styles.monitoringHeader}>
              <div style={styles.readOnlyBanner}>
                <Eye size={16} />
                <span style={{ fontWeight: '600', fontSize: '0.85rem' }}>
                  READ ONLY - Monitoring Mode
                </span>
              </div>

              <div style={styles.conversationHeader}>
                <div>
                  <div style={styles.conversationTitle}>
                    {selectedConversation.user?.name || 'Unknown User'}
                  </div>
                  <div style={styles.conversationSubtitle}>
                    with Agent {selectedConversation.helper?.name || 'Unknown Agent'}
                    {selectedConversation.helper?.agentInfo?.agency && 
                      ` (${selectedConversation.helper.agentInfo.agency})`
                    }
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.messagesContainer}>
              {isLoadingMessages ? (
                <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', padding: '40px' }}>
                  Loading messages...
                </div>
              ) : messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)', padding: '40px' }}>
                  No messages yet
                </div>
              ) : (
                messages.map((msg) => {
                  const isAgent = msg.senderRole === 'agent';
                  return (
                    <div key={msg._id} style={styles.message(isAgent)}>
                      <div style={styles.messageBubble(isAgent)}>
                        <div style={styles.messageHeader}>
                          {isAgent ? 'Agent' : 'User'} • {msg.sender.name}
                        </div>
                        <div style={styles.messageContent}>
                          {msg.content}
                        </div>
                        <div style={styles.messageTime}>
                          {new Date(msg.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AgentMonitoring;